import type { TranslationKey } from "@/src/i18n";
import type { FloorObjectCategory, FloorObjectType } from "@/src/types/map";

/**
 * How the renderer draws a floor object. Declarative for the same reason as
 * WallObjectStyle: a new type should be a catalogue entry plus a draw branch,
 * not a new placement path.
 */
export interface FloorObjectStyle {
  /** Tread count for stairs and ramps. */
  steps?: number;
  /** Direction arrow along the object's long axis. */
  direction?: "up" | "down";
  /** Spiral staircase: arc plus radial treads. */
  spiral?: boolean;
  /** Ladder rung count. */
  rungs?: number;
  /** Square hatch with a hinge; "ceiling" is drawn dashed. */
  hatch?: "floor" | "ceiling";
  /** Pit treatment. */
  pit?: "open" | "covered" | "spiked";
  /** Concentric rings, for a shaft or well. */
  rings?: number;
  /** Tapered wedge, for a ramp. */
  wedge?: boolean;
}

export interface FloorObjectDefinition {
  id: FloorObjectType;
  category: FloorObjectCategory;
  /** Grid-snapped and freely rotatable, unlike the wall-snapped door family. */
  placement: "grid-snap";
  defaultWidthCells: number;
  defaultHeightCells: number;
  style: FloorObjectStyle;
  labelKey: TranslationKey;
  dmOnly: boolean;
}

export const FLOOR_OBJECT_CATALOG: FloorObjectDefinition[] = [
  // --- stairs & slopes -----------------------------------------------------
  { id: "stairs_up", category: "stairs", placement: "grid-snap", defaultWidthCells: 2, defaultHeightCells: 1,
    style: { steps: 5, direction: "up" }, labelKey: "floor.stairs_up", dmOnly: false },
  { id: "stairs_down", category: "stairs", placement: "grid-snap", defaultWidthCells: 2, defaultHeightCells: 1,
    style: { steps: 5, direction: "down" }, labelKey: "floor.stairs_down", dmOnly: false },
  { id: "spiral", category: "stairs", placement: "grid-snap", defaultWidthCells: 1.2, defaultHeightCells: 1.2,
    style: { spiral: true, steps: 8 }, labelKey: "floor.spiral", dmOnly: false },
  { id: "ramp", category: "stairs", placement: "grid-snap", defaultWidthCells: 2, defaultHeightCells: 1,
    style: { wedge: true, direction: "up" }, labelKey: "floor.ramp", dmOnly: false },

  // --- access --------------------------------------------------------------
  { id: "ladder", category: "access", placement: "grid-snap", defaultWidthCells: 0.6, defaultHeightCells: 1.6,
    style: { rungs: 5 }, labelKey: "floor.ladder", dmOnly: false },
  { id: "shaft", category: "access", placement: "grid-snap", defaultWidthCells: 1.2, defaultHeightCells: 1.2,
    style: { rings: 3 }, labelKey: "floor.shaft", dmOnly: false },

  // --- hatches -------------------------------------------------------------
  { id: "trapdoor_floor", category: "hatches", placement: "grid-snap", defaultWidthCells: 1, defaultHeightCells: 1,
    style: { hatch: "floor" }, labelKey: "floor.trapdoor_floor", dmOnly: false },
  { id: "trapdoor_ceiling", category: "hatches", placement: "grid-snap", defaultWidthCells: 1, defaultHeightCells: 1,
    style: { hatch: "ceiling" }, labelKey: "floor.trapdoor_ceiling", dmOnly: true },

  // --- hazards -------------------------------------------------------------
  { id: "pit_open", category: "hazards", placement: "grid-snap", defaultWidthCells: 1.4, defaultHeightCells: 1.4,
    style: { pit: "open" }, labelKey: "floor.pit_open", dmOnly: false },
  { id: "pit_covered", category: "hazards", placement: "grid-snap", defaultWidthCells: 1.4, defaultHeightCells: 1.4,
    style: { pit: "covered" }, labelKey: "floor.pit_covered", dmOnly: true },
  { id: "pit_spiked", category: "hazards", placement: "grid-snap", defaultWidthCells: 1.4, defaultHeightCells: 1.4,
    style: { pit: "spiked" }, labelKey: "floor.pit_spiked", dmOnly: false },
];

/**
 * What a pre-v3 `stairs` stamp becomes. The original had no direction at all, so
 * either choice is arbitrary; "down" is recorded here rather than hidden in the
 * migrator so it is obvious, and the picker can flip any migrated stair in one click.
 */
export const DEFAULT_FLOOR_TYPE: FloorObjectType = "stairs_down";

const BY_ID = new Map(FLOOR_OBJECT_CATALOG.map((entry) => [entry.id, entry]));

export const getFloorObject = (type: FloorObjectType | undefined): FloorObjectDefinition =>
  (type ? BY_ID.get(type) : undefined) ?? FLOOR_OBJECT_CATALOG[1]!;

export const isFloorObjectType = (value: unknown): value is FloorObjectType =>
  typeof value === "string" && BY_ID.has(value as FloorObjectType);

export const FLOOR_OBJECT_CATEGORIES: readonly FloorObjectCategory[] =
  ["stairs", "access", "hatches", "hazards"];

/** The asset id the whole floor-object family lives on. */
export const FLOOR_OBJECT_ASSET = "stairs" as const;

/** Rotation step for the R shortcut, in degrees. */
export const FLOOR_ROTATION_STEP = 45;
